/**
 * On-Site Area Report — PF v2 configuration UI.
 *
 * Ports the REDESIGNED legacy step (`MeasurementStepRedesign`, ISSUE-2187 / epic
 * ISSUE-1595), not the original one: property-type cards, per-unit residential
 * builder, cellar/attic scopes, the two paid add-ons, and live price hints from
 * the rate card. The presentational pieces are copied into `./components` so the
 * module owns its UI and imports nothing from `PurchaseFlow/*`.
 *
 * Since ISSUE-2188 the cart carries no measured-extra products — the engine
 * derives them from this configuration — so every input here is a price input,
 * and the hints quote the same rate card the engine bills from.
 */

import React, { useCallback, useMemo } from 'react'
import { Stack, Typography } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'

import { GRAY_300, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { Label } from 'components/common/Label'
import { MUIInputField, StyledMUIInput } from 'components/common/MUIInputField'
import { MUIInputFieldButton } from 'components/common/MUIInputField/MUIInputFieldButton'
import { MUIInputFieldCounter } from 'components/common/MUIInputFieldCounter'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { CheckboxItem } from 'components/common/CheckboxItem'
import {
  PropertyTypeCards,
  ResidentialUnitsBuilder
} from 'components/common/MeasurementControls'
import { MUICheckCard } from 'components/common/MUICards/MUICheckCard'
import { SelectableButtonsGrid, SelectableButtonsGridValue } from 'components/common/SelectableButtonsGrid'
import { formatPrice } from 'utils/price'
import {
  AtticScope,
  CellarScope,
  MeasurementOnSiteAdditionalInformationType,
  MeasurementOnSitePropertyType
} from 'models/purchaseFlow/measurementDTO'
import type { CartItemId } from 'models/purchaseFlowRevamp'
import { usePfMeasurementPricing } from 'dataQueries/purchaseFlowRevampMeasurement.query'

import { atticLevelRate, cellarWholeRate } from 'components/common/MeasurementControls/measurementHintPricing'
import {
  defaultFloors,
  forPropertyType,
  isUnitBasedPropertyType,
  minimumUnits
} from './measurement.dto'
import { useMeasurementConfig } from './useMeasurementConfig'

/** The included-floor count the "2nd floor / 3rd+" hint ordinals are written for. */
const HOUSE_FLOOR_HINT_COPY_INCLUDED_FLOORS = 2

export interface MeasurementProps {
  cartItemId: CartItemId
}

export const Measurement: React.FC<MeasurementProps> = ({ cartItemId }) => {
  // Every key in this module is written relative to `measurement_step`, as in legacy.
  const { t } = useTranslation(['purchase_flow'], { keyPrefix: 'measurement_step' })
  const { value, patch } = useMeasurementConfig(cartItemId)

  // `api.get` resolves to the Axios envelope; the rate card is its body.
  const pricing = usePfMeasurementPricing().data?.data

  const unitBased = isUnitBasedPropertyType(value.propertyType)
  const isHouseType = value.propertyType === MeasurementOnSitePropertyType.SINGLE_FAMILY_HOUSE
  const isTwoFamilyHouseType = value.propertyType === MeasurementOnSitePropertyType.SEMI_DETACHED_HOUSE

  // The hint's ordinals hard-code the included-floor count, so a backend retune
  // would leave the amounts right and the words wrong — hide it instead.
  const isHouseFloorHintAligned =
    !pricing || pricing.houseFloorsIncluded === HOUSE_FLOOR_HINT_COPY_INCLUDED_FLOORS

  const levelConfig = useMemo(() => ({
    propertyType: value.propertyType,
    totalFloors: unitBased
      ? value.residentialUnits.reduce((sum, floors) => sum + floors, 0)
      : value.numberOfFloors,
    cellarScope: value.cellarScope,
  }), [value.propertyType, unitBased, value.residentialUnits, value.numberOfFloors, value.cellarScope])

  const additionalUnitPrice = pricing ? formatPrice(pricing.additionalResidentialUnit) : null
  const expressServicePrice = pricing?.expressService ? formatPrice(pricing.expressService) : null
  const houseViewsPrice = pricing?.additionalHouseViews ? formatPrice(pricing.additionalHouseViews) : null

  /**
   * A single-floor house with nothing chargeable is priced by the engine at the
   * apartment rate, so the card would claim a category the client is not billed
   * for. Collapse it instead of showing a house billed as an apartment.
   */
  const collapseBareSingleFloorHouse = useCallback(
    (floors: number, cellar: CellarScope, attic: AtticScope): boolean =>
      isHouseType && floors === 1 && cellar !== CellarScope.WHOLE && attic !== AtticScope.MEASURED,
    [isHouseType]
  )

  const setPropertyType = useCallback((propertyType: MeasurementOnSitePropertyType) => {
    patch(forPropertyType(value, propertyType))
  }, [patch, value])

  const handleFloorsChange = useCallback((numberOfFloors: number) => {
    if (collapseBareSingleFloorHouse(numberOfFloors, value.cellarScope, value.atticScope)) {
      patch(forPropertyType({ ...value, numberOfFloors }, MeasurementOnSitePropertyType.APARTMENT_SINGLE_FLOORS))
      return
    }
    patch({ numberOfFloors })
  }, [collapseBareSingleFloorHouse, patch, value])

  const applyScope = useCallback((next: Partial<typeof value>) => {
    const merged = { ...value, ...next }
    if (collapseBareSingleFloorHouse(merged.numberOfFloors, merged.cellarScope, merged.atticScope)) {
      patch(forPropertyType(merged, MeasurementOnSitePropertyType.APARTMENT_SINGLE_FLOORS))
      return
    }
    patch(next)
  }, [collapseBareSingleFloorHouse, patch, value])

  /** Adding past a two-family house's second unit makes it a multi-unit building. */
  const handleAddUnit = useCallback(() => {
    const units = [...value.residentialUnits, 1]
    if (isTwoFamilyHouseType) {
      patch({ propertyType: MeasurementOnSitePropertyType.APARTMENT_BUILDING, residentialUnits: units })
      return
    }
    patch({ residentialUnits: units })
  }, [isTwoFamilyHouseType, patch, value.residentialUnits])

  /** Dropping below a category's minimum lands in the category below it. */
  const handleRemoveUnit = useCallback((unitIndex: number) => {
    const remaining = value.residentialUnits.filter((_, index) => index !== unitIndex)
    if (remaining.length >= minimumUnits(value.propertyType)) {
      patch({ residentialUnits: remaining })
      return
    }
    if (value.propertyType === MeasurementOnSitePropertyType.APARTMENT_BUILDING) {
      patch({ propertyType: MeasurementOnSitePropertyType.SEMI_DETACHED_HOUSE, residentialUnits: remaining })
      return
    }
    patch({
      propertyType: MeasurementOnSitePropertyType.SINGLE_FAMILY_HOUSE,
      residentialUnits: [],
      numberOfFloors: defaultFloors(MeasurementOnSitePropertyType.SINGLE_FAMILY_HOUSE),
    })
  }, [patch, value.propertyType, value.residentialUnits])

  const toggleAdditionalInformation = useCallback((type: MeasurementOnSiteAdditionalInformationType) => {
    const selected = value.additionalInformation.includes(type)
    patch({
      additionalInformation: selected
        ? value.additionalInformation.filter((item) => item !== type)
        : [...value.additionalInformation, type],
    })
  }, [patch, value.additionalInformation])

  const cellarScopeOptions: SelectableButtonsGridValue<CellarScope>[] = [
    { value: CellarScope.ROOM, displayName: t('property_details.cellar_scope_room') },
    {
      value: CellarScope.WHOLE,
      displayName: (
        <>
          {t('property_details.cellar_scope_whole')}{' '}
          {!!pricing &&
            <Trans
              t={t}
              i18nKey="property_details.extra_price"
              values={{ price: formatPrice(cellarWholeRate(levelConfig, pricing), pricing.currency) }}
              components={{ strong: <strong /> }}
            />
          }
        </>
      ),
    },
    { value: CellarScope.PRESENT_NOT_MEASURED, displayName: t('property_details.scope_not_measured') },
  ]

  const atticScopeOptions: SelectableButtonsGridValue<AtticScope>[] = [
    {
      value: AtticScope.MEASURED,
      displayName: (
        <>
          {t('property_details.attic_scope_measured')}{' '}
          {!!pricing &&
            <Trans
              t={t}
              i18nKey="property_details.extra_price"
              values={{ price: formatPrice(atticLevelRate(levelConfig, pricing), pricing.currency) }}
              components={{ strong: <strong /> }}
            />
          }
        </>
      ),
    },
    { value: AtticScope.PRESENT_NOT_MEASURED, displayName: t('property_details.scope_not_measured') },
  ]

  const addUnitCaption = isTwoFamilyHouseType
    ? t(
      additionalUnitPrice ? 'property_details.add_extra_unit_switch_price' : 'property_details.add_extra_unit_switch',
      { price: additionalUnitPrice }
    )
    : additionalUnitPrice ? t('property_details.add_extra_unit_price', { price: additionalUnitPrice }) : undefined

  return (
    <Stack gap="1.6rem">

      <PropertyTypeCards selectedType={value.propertyType} onSelect={setPropertyType} />

      <SectionedBorderBox title={t('property_details.title')}>
        <Stack gap="1.8rem">

          <Stack direction="row" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap=".8rem">

            <MUIInputField
              required
              inputBoxWidth="16rem"
              label={t('property_details.total_floor_area')}
              customInput={
                <>
                  <StyledMUIInput
                    value={value.surfaceArea ?? ''}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '')
                      patch({ surfaceArea: digits ? parseInt(digits) : null })
                    }}
                  />
                  <MUIInputFieldButton sx={{ padding: '0 1.2rem' }}>
                    <Typography variant="text-md" color={GRAY_900} fontWeight="medium">m²</Typography>
                  </MUIInputFieldButton>
                </>
              }
            />

            {!!pricing &&
              <Typography variant="text-sm" color={GRAY_700} paddingBottom="1rem" component="div">
                <Trans
                  t={t}
                  i18nKey="property_details.total_floor_area_hint"
                  values={{
                    price: formatPrice(pricing.areaSurchargeRatePerSqm),
                    threshold: pricing.areaSurchargeThresholdSqm,
                  }}
                  components={{ strong: <strong /> }}
                />
              </Typography>
            }

          </Stack>

          {!unitBased &&
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap=".8rem">

              <MUIInputFieldCounter
                minValue={1}
                counterValue={value.numberOfFloors}
                counterOnChange={handleFloorsChange}
                inputBoxWidth="14rem"
                label={t('property_details.number_of_floors')}
              />

              {!!pricing && (!isHouseType || isHouseFloorHintAligned) &&
                <Typography variant="text-sm" color={GRAY_700} paddingBottom="1rem" component="div">
                  {isHouseType
                    ? <Trans
                      t={t}
                      i18nKey="property_details.house_floor_price_hint"
                      values={{
                        secondFloorPrice: formatPrice(pricing.housePerFloor),
                        price: formatPrice(pricing.additionalFloor),
                      }}
                      components={{ strong: <strong /> }}
                    />
                    : <Trans
                      t={t}
                      i18nKey="property_details.floor_price_hint"
                      values={{ price: formatPrice(pricing.additionalFloor) }}
                      components={{ strong: <strong /> }}
                    />
                  }
                </Typography>
              }

            </Stack>
          }

          {unitBased &&
            <ResidentialUnitsBuilder
              units={value.residentialUnits}
              minUnits={1}
              extraUnitFromIndex={minimumUnits(value.propertyType)}
              extraUnitPriceFormatted={additionalUnitPrice}
              addUnitCaption={addUnitCaption}
              floorPriceHint={pricing &&
                <Trans
                  t={t}
                  i18nKey="property_details.unit_floor_price_hint"
                  values={{ price: formatPrice(pricing.additionalFloor) }}
                  components={{ strong: <strong /> }}
                />
              }
              onUnitsChange={(residentialUnits) => patch({ residentialUnits })}
              onAddUnit={handleAddUnit}
              onRemoveUnit={handleRemoveUnit}
            />
          }

          <Stack gap=".6rem">

            <Label text={t('property_information.additional_area')} />

            <Stack gap=".8rem">

              {/* Cellar before attic: a measured cellar moves the attic to the
                  building's third level and changes its price, so the attic pill
                  can only quote the real amount once the cellar is answered. */}
              <MUICheckCard
                title={t('property_details.cellar_title')}
                description={t('property_details.cellar_description')}
                checked={value.cellarScope !== CellarScope.NONE}
                onCheck={() => applyScope({
                  cellarScope: value.cellarScope === CellarScope.NONE ? CellarScope.WHOLE : CellarScope.NONE,
                })}
                expandableContent={
                  <Stack gap=".6rem" sx={{ borderTop: `1px solid ${GRAY_300}`, paddingTop: '1.2rem' }}>
                    <Typography variant="text-sm" color={GRAY_700}>{t('property_details.cellar_scope_question')}</Typography>
                    <SelectableButtonsGrid
                      values={cellarScopeOptions}
                      selectedValue={value.cellarScope}
                      onSelect={(cellarScope) => applyScope({ cellarScope })}
                    />
                  </Stack>
                }
              />

              <MUICheckCard
                title={t('property_details.attic_title')}
                description={t('property_details.attic_description')}
                checked={value.atticScope !== AtticScope.NONE}
                onCheck={() => applyScope({
                  atticScope: value.atticScope === AtticScope.NONE ? AtticScope.MEASURED : AtticScope.NONE,
                })}
                expandableContent={
                  <Stack gap=".6rem" sx={{ borderTop: `1px solid ${GRAY_300}`, paddingTop: '1.2rem' }}>
                    <Typography variant="text-sm" color={GRAY_700}>{t('property_details.attic_scope_question')}</Typography>
                    <SelectableButtonsGrid
                      values={atticScopeOptions}
                      selectedValue={value.atticScope}
                      onSelect={(atticScope) => applyScope({ atticScope })}
                    />
                  </Stack>
                }
              />

            </Stack>

          </Stack>

          {(!!expressServicePrice || !!houseViewsPrice) &&
            <Stack gap=".6rem">

              <Label text={t('additional_options.title')} />

              <Stack gap=".8rem">

                {!!expressServicePrice &&
                  <MUICheckCard
                    title={t('additional_options.express_title')}
                    description={t('additional_options.express_description')}
                    checked={value.expressService}
                    trailing={
                      <Typography variant="text-sm" color={GRAY_700} whiteSpace="nowrap" component="div">
                        <Trans
                          t={t}
                          i18nKey="additional_options.add_on_price"
                          values={{ price: expressServicePrice }}
                          components={{ strong: <strong /> }}
                        />
                      </Typography>
                    }
                    onCheck={() => patch({ expressService: !value.expressService })}
                  />
                }

                {!!houseViewsPrice &&
                  <MUICheckCard
                    title={t('additional_options.house_views_title')}
                    description={t('additional_options.house_views_description')}
                    checked={value.additionalHouseViews}
                    trailing={
                      <Typography variant="text-sm" color={GRAY_700} whiteSpace="nowrap" component="div">
                        <Trans
                          t={t}
                          i18nKey="additional_options.add_on_price"
                          values={{ price: houseViewsPrice }}
                          components={{ strong: <strong /> }}
                        />
                      </Typography>
                    }
                    onCheck={() => patch({ additionalHouseViews: !value.additionalHouseViews })}
                  />
                }

              </Stack>

            </Stack>
          }

          <Stack gap=".6rem">

            <Label text={t('additional_information.description')} />

            <Stack gap=".4rem">
              {Object.values(MeasurementOnSiteAdditionalInformationType).map((type) => (
                <CheckboxItem
                  key={type}
                  title={t(`additional_information_type.${type}`)}
                  checked={value.additionalInformation.includes(type)}
                  onClick={() => toggleAdditionalInformation(type)}
                />
              ))}
            </Stack>

          </Stack>

          <Stack gap=".6rem">

            <Label text={t('comments.title')} />

            <MUIInputField
              isMultiline
              placeholder={t('property_details.comments_placeholder')}
              value={value.comment}
              onChange={(e) => patch({ comment: e.target.value })}
            />

          </Stack>

        </Stack>
      </SectionedBorderBox>

    </Stack>
  )
}
